import { useState, useRef, useEffect } from "react";
import { APIProvider, useMapsLibrary, useApiLoadingStatus, APILoadingStatus } from "@vis.gl/react-google-maps";
import { MapPin, LocateFixed } from "lucide-react";

const CYAN = "var(--cyan)";
const ELEV = "var(--elevated)";
const WHITE = "var(--text)";
const MUTED = "var(--text-muted)";
const YELLOW = "#F59E0B";
const FB = "'Outfit', sans-serif";

function Field({ value, onChange, onValidChange, onCoords, onSubmit, inputStyle }) {
  const places = useMapsLibrary("places");
  const geocoding = useMapsLibrary("geocoding");
  const apiStatus = useApiLoadingStatus();
  const sdkFailed = apiStatus === APILoadingStatus.FAILED || apiStatus === APILoadingStatus.AUTH_FAILURE;
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locErr, setLocErr] = useState("");
  const [lookupError, setLookupError] = useState(false);
  const tokenRef = useRef(null);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (sdkFailed) onValidChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkFailed]);

  const fetchSug = (q) => {
    onChange(q);
    onValidChange(false);
    onCoords(null);
    setLocErr("");
    if (timer.current) clearTimeout(timer.current);
    if (!places || q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        if (!tokenRef.current) tokenRef.current = new places.AutocompleteSessionToken();
        const { suggestions: sug } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: q,
          sessionToken: tokenRef.current,
          includedRegionCodes: ["ca"],
          locationBias: { center: { lat: 42.42, lng: -82.1 }, radius: 50000 },
        });
        const opts = (sug || [])
          .filter((s) => s.placePrediction)
          .map((s) => ({ text: s.placePrediction.text.text, prediction: s.placePrediction }));
        setSuggestions(opts);
        setOpen(true);
        setLookupError(false);
      } catch (e) {
        setSuggestions([]);
        setLookupError(true);
      }
    }, 250);
  };

  const pick = async (item) => {
    const text = typeof item === "string" ? item : item.text;
    onChange(text);
    setSuggestions([]);
    setOpen(false);
    tokenRef.current = null;

    // 1) Most precise: resolve the selected Place's exact location (rooftop / parcel point)
    try {
      if (item && item.prediction && typeof item.prediction.toPlace === "function") {
        const place = item.prediction.toPlace();
        await place.fetchFields({ fields: ["location"] });
        if (place.location) {
          onCoords({ lat: place.location.lat(), lng: place.location.lng() });
          onValidChange(true);
          return;
        }
      }
    } catch (e) {
      // fall through to geocoder
    }

    // 2) Fallback: geocode the address, preferring a ROOFTOP result over a road-interpolated one
    if (geocoding) {
      try {
        const geocoder = new geocoding.Geocoder();
        const { results } = await geocoder.geocode({ address: text });
        if (results && results.length) {
          const rooftop = results.find((r) => r.geometry && r.geometry.location_type === "ROOFTOP");
          const best = rooftop || results[0];
          const loc = best.geometry.location;
          onCoords({ lat: loc.lat(), lng: loc.lng() });
          onValidChange(true);
          return;
        }
      } catch (e) {
        // fall through to valid-without-coords
      }
    }
    onCoords(null);
    onValidChange(true);
  };

  const useMyLocation = () => {
    setLocErr("");
    if (!navigator.geolocation) {
      setLocErr("Location isn't supported on this device — please type your address.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          if (!geocoding) {
            setLocErr("Still loading — try again in a moment.");
            return;
          }
          const geocoder = new geocoding.Geocoder();
          const { results } = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });
          if (results && results[0]) {
            onChange(results[0].formatted_address);
            onCoords({ lat: latitude, lng: longitude });
            onValidChange(true);
            setOpen(false);
            setSuggestions([]);
          } else {
            setLocErr("Couldn't find your address — please type it.");
          }
        } catch (e) {
          setLocErr("Couldn't detect your address — please type it.");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        setLocErr(
          err && err.code === 1
            ? "Location permission denied — please type your address."
            : "Couldn't get your location — please type it."
        );
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const failed = sdkFailed || lookupError;

  return (
    <div ref={boxRef}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          value={value}
          onChange={(e) => fetchSug(e.target.value)}
          onFocus={() => { if (suggestions.length) setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setOpen(false); onSubmit(); } }}
          placeholder="Enter your address…"
          data-testid="address-input"
          autoComplete="off"
          style={inputStyle}
        />
        <MapPin size={17} color={MUTED} style={{ position: "absolute", right: 15, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
        {open && suggestions.length > 0 && (
          <div data-testid="address-suggestions" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 30, background: ELEV, border: "1px solid var(--w14)", borderRadius: 12, overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.6)" }}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => pick(s)}
                data-testid={"address-suggestion-" + i}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "12px 16px", background: "transparent", border: "none", borderBottom: "1px solid var(--w6)", color: WHITE, fontFamily: FB, fontSize: 14, cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--w5)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <MapPin size={14} color={CYAN} style={{ flexShrink: 0 }} /> {s.text}
              </button>
            ))}
            <div style={{ padding: "7px 16px", fontFamily: FB, fontSize: 10, color: MUTED, textAlign: "right", letterSpacing: "0.02em" }}>Powered by Google</div>
          </div>
        )}
      </div>

      {!failed && (
        <button
          type="button"
          onClick={useMyLocation}
          data-testid="use-location-btn"
          disabled={locating}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, marginTop: 10, padding: "4px 2px", background: "none", border: "none", color: CYAN, fontFamily: FB, fontSize: 13, fontWeight: 500, cursor: locating ? "default" : "pointer", opacity: locating ? 0.6 : 1 }}
        >
          <LocateFixed size={14} strokeWidth={2.2} /> {locating ? "Locating…" : "Use my current location"}
        </button>
      )}
      {failed && (
        <p data-testid="sdk-error" style={{ margin: "10px 0 0", fontFamily: FB, fontSize: 12, color: YELLOW, lineHeight: 1.4 }}>Address lookup is temporarily unavailable — just type your address and hit Check Availability.</p>
      )}
      {locErr && !failed && (
        <p data-testid="location-error" style={{ margin: "8px 0 0", fontFamily: FB, fontSize: 12, color: YELLOW, lineHeight: 1.4 }}>{locErr}</p>
      )}
    </div>
  );
}

export default function AddressAutocomplete({ value, onChange, onValidChange, onCoords, onSubmit, inputStyle }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  return (
    <APIProvider apiKey={apiKey}>
      <Field value={value} onChange={onChange} onValidChange={onValidChange} onCoords={onCoords} onSubmit={onSubmit} inputStyle={inputStyle} />
    </APIProvider>
  );
}
